import { memo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useWindowBreakpoint from "../../../hooks/use_window_breakpoint";
import { useAppContext } from "../../../app_context";
import Tooltip from "../rail_tooltip";
import { useTooltip } from "../../../common/components/tooltip";
import type { MenuItem } from "./nav_item.types";

const COLLAPSED_ITEM_CLASS = [
	"relative inline-flex items-center justify-center w-[38px] h-[38px] shrink-0 rounded-[8px]",
	"transition-all duration-200 cursor-pointer [-webkit-tap-highlight-color:transparent]",
	"[touch-action:manipulation] no-underline",
	"[&_svg]:w-[19px] [&_svg]:h-[19px] [&_svg]:[flex:0_0_19px]",
	"[&_svg]:transition-[opacity] [&_svg]:duration-200 [&_svg]:[pointer-events:none]",
].join(" ");

const COLLAPSED_ITEM_ACTIVE_CLASS = [
	"bg-primary/15 text-primary",
	"[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary-light",
	"[@media(hover:hover)_and_(pointer:fine)]:dark:hover:bg-primary/20",
].join(" ");

const COLLAPSED_ITEM_INACTIVE_CLASS = [
	"bg-transparent text-nav-inactive dark:text-text-dark-muted",
	"[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/15",
	"[@media(hover:hover)_and_(pointer:fine)]:dark:hover:bg-primary/15",
].join(" ");

// The phone's bottom bar, from the designer's prototype: icon over label, every item
// labelled rather than only the current one, and no pill behind the active item —
// colour and a heavier label carry it. Items share the width equally, so the row reads
// as a set of destinations instead of one highlighted chip among plain icons.
const STACKED_ITEM_CLASS = [
	"relative flex flex-1 flex-col items-center justify-center gap-[2px]",
	"px-0 pt-[6px] pb-[4px] font-['Inter'] text-center no-underline bg-transparent",
	"transition-colors duration-200 [-webkit-tap-highlight-color:transparent]",
	"[touch-action:manipulation] cursor-pointer",
	"[&_svg]:w-[21px] [&_svg]:h-[21px] [&_svg]:[flex:0_0_auto] [&_svg]:[pointer-events:none]",
].join(" ");

const getStackedLabelClass = (active: boolean) =>
	["text-[10.5px] leading-[normal]", active ? "font-semibold" : "font-medium"].join(" ");

// Tailwind scans class strings statically, so an interpolated `leading-[${x}]` never
// reaches the build. Every line height a menu item may ask for is spelled out here.
const LINE_HEIGHT_CLASS: Record<string, string> = {
	default: "leading-[150%]",
	"140%": "leading-[140%]",
	"150%": "leading-[150%]",
};

interface NavItemClassArgs {
	stacked: boolean;
	fullWidth: boolean;
	centered: boolean;
	active: boolean;
	lineHeight?: string;
	collapsed: boolean;
}

const getNavItemClass = ({
	stacked,
	fullWidth,
	centered,
	active,
	lineHeight,
	collapsed,
}: NavItemClassArgs) => {
	if (collapsed) {
		return [
			COLLAPSED_ITEM_CLASS,
			active ? COLLAPSED_ITEM_ACTIVE_CLASS : COLLAPSED_ITEM_INACTIVE_CLASS,
		].join(" ");
	}
	if (stacked) {
		return [
			STACKED_ITEM_CLASS,
			active ? "text-primary" : "text-nav-inactive dark:text-text-dark-muted",
		].join(" ");
	}
	const padding = fullWidth
		? "px-[10px] @max-[210px]:px-[8px] @max-[176px]:px-[6px]"
		: active
			? "px-[16px]"
			: "px-[6px]";
	const textColor = fullWidth
		? active
			? "text-primary"
			: "text-nav-inactive dark:text-text-dark-muted"
		: "text-primary";
	const borderRadius = active ? (fullWidth ? "rounded-md" : "rounded-[25px]") : "rounded-[7px]";
	const bg = active ? "bg-primary/15" : "bg-transparent";
	// Hover is reserved for the persistent desktop sidebar (fullWidth) — the mobile/tablet
	// top and bottom bars use the same item component but must stay hover-free.
	const hoverBg = !fullWidth
		? ""
		: active
			? "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary-light [@media(hover:hover)_and_(pointer:fine)]:dark:hover:bg-primary/20"
			: "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/15 [@media(hover:hover)_and_(pointer:fine)]:dark:hover:bg-primary/15";
	// Both branches state the size outright. Phosphor draws at 1em, so without this the
	// glyphs would take the label's 14.5px and come out visibly smaller than the icons
	// they replaced; 21px is the prototype's bottom-bar size and matches what the older
	// hand-drawn icons carried in their own width/height.
	const svgSize = fullWidth
		? "[&_svg]:w-[19px] [&_svg]:h-[19px] [&_svg]:[flex:0_0_19px]"
		: "[&_svg]:w-[21px] [&_svg]:h-[21px] [&_svg]:[flex:0_0_auto]";
	const svgOpacity = "[&_svg]:opacity-100";

	return [
		// Icon beside label; the icon-over-label row is the `stacked` branch above.
		// The open sidebar's rows are 40px tall, as in the prototype; the top bar keeps 38.
		"relative inline-flex flex-row items-center",
		fullWidth ? "min-h-[40px]" : "min-h-[38px]",
		fullWidth ? "gap-[12px] w-full" : "gap-[4px] w-auto",
		centered ? "justify-center" : "justify-start",
		padding,
		// In the open sidebar only the current item is medium; the rest are regular.
		"font-['Inter'] text-[14.5px] text-center no-underline",
		fullWidth && !active ? "font-normal" : "font-medium",
		LINE_HEIGHT_CLASS[lineHeight ?? "default"] ?? LINE_HEIGHT_CLASS.default,
		textColor,
		borderRadius,
		bg,
		"transition-all duration-200 [-webkit-tap-highlight-color:transparent] [touch-action:manipulation] cursor-pointer",
		hoverBg,
		fullWidth ? "[@media(hover:hover)_and_(pointer:fine)]:hover:[&_svg]:opacity-100" : "",
		svgSize,
		svgOpacity,
		"[&_svg]:transition-[opacity] [&_svg]:duration-200 [&_svg]:[pointer-events:none]",
	].join(" ");
};

const getBadgeClass = (inline: boolean) =>
	[
		inline ? "static" : "absolute top-[-2px] right-[-4px]",
		"flex items-center justify-center min-w-[16px] h-[16px] px-[3px] rounded-full",
		"bg-gold text-black text-[10px] leading-[16px] font-medium",
		"[box-shadow:0_1px_2px_rgba(0,0,0,0.15)] pointer-events-none",
	].join(" ");

interface Props {
	menuItem: MenuItem;
	isHeaderMoreMenuOpen?: boolean;
	unread?: number | null;
	showLabelWhenInactive?: boolean;
	// The persistent desktop sidebar: full-width rows, hover states, no tooltip.
	fullWidth?: boolean;
	centered?: boolean;
	enableTooltip?: boolean;
	// The collapsed icon rail: no label, tooltip on every item including the active one.
	collapsed?: boolean;
	disabled?: boolean;
	// Rail-only: prefixes the tooltip so admin icons are told apart from the rest.
	admin?: boolean;
}

const HeaderMenuItem = ({
	menuItem,
	isHeaderMoreMenuOpen,
	unread = null,
	showLabelWhenInactive = false,
	fullWidth = false,
	centered = false,
	enableTooltip = true,
	collapsed = false,
	disabled = false,
	admin = false,
}: Props) => {
	const tip = useTooltip();
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const isMobile = useWindowBreakpoint(500);
	const { siteColorTheme } = useAppContext();
	const isDark = siteColorTheme === "DARK";

	const {
		path,
		// Which paths count as "on this section" for highlighting/label purposes.
		// Defaults to [path] — a menu item that also owns a legacy/alias route (the
		// Bible nav item does, see header/index.jsx) lists every one of them.
		activePaths = path ? [path] : [],
		icon: Icon,
		onClick,
		isActive: forcedActive,
		textKey,
		lineHeight,
	} = menuItem;

	// When the More menu is open, useOnclickOutside intercepts mousedown and closes
	// the menu, but the subsequent click event may never reach the Link. We navigate
	// programmatically on mousedown and block the redundant click navigation.
	const didNavigateOnMouseDown = useRef(false);

	const handleMenuMouseDown = useCallback(() => {
		if (path) {
			didNavigateOnMouseDown.current = true;
			navigate(path);
		}
	}, [path, navigate]);

	const handleMenuClick = useCallback(e => {
		if (didNavigateOnMouseDown.current) {
			didNavigateOnMouseDown.current = false;
			e.preventDefault();
		}
	}, []);

	const label = textKey ? t(textKey) : menuItem.text || "";

	const isActiveByPath = activePaths.some(p =>
		p === "/"
			? location.pathname === "/"
			: location.pathname === p || location.pathname.startsWith(p + "/"),
	);

	const isActive = forcedActive ?? isActiveByPath;

	const isHighlightDisabled = isMobile && isHeaderMoreMenuOpen;
	const isActuallyActive = isActive && !isHighlightDisabled;
	// Below 500px this row is the bottom bar, where the prototype labels every item.
	// Wider, the same component is the guest top bar and keeps its single active pill.
	const stacked = !fullWidth && !collapsed && isMobile;
	const showLabel = !collapsed && (stacked || isActuallyActive || showLabelWhenInactive);
	const tooltip = !collapsed && enableTooltip && !isActuallyActive ? label : undefined;
	// On the rail the admin items are just more icons: the "Admin panel" heading
	// they sit under in the open sidebar is not there to tell them apart, so the
	// tooltip carries it instead.
	const railLabel = admin ? t("app-header.sidebar.admin-item-tooltip", { label }) : label;
	const iconColor =
		fullWidth || collapsed || stacked
			? isActuallyActive
				? "var(--color-primary)"
				: isDark
					? "#9e9cb8"
					: stacked
						? "var(--color-nav-inactive)"
						: "var(--color-icon-default)"
			: "var(--color-primary)";

	const itemClass = [
		getNavItemClass({
			stacked,
			fullWidth,
			centered,
			active: isActuallyActive,
			lineHeight,
			collapsed,
		}),
		disabled ? "opacity-40 cursor-not-allowed pointer-events-none" : "",
	].join(" ");

	const content = (
		<>
			{/* No `$active`: Phosphor forwards unknown props straight to the <svg>, and React
			    rejected the transient prop on every render. Nothing read it — colour already
			    comes from `iconColor`. */}
			<Icon fill={iconColor} stroke={iconColor} />
			{showLabel &&
				(stacked ? (
					<span className={getStackedLabelClass(isActuallyActive)}>{label}</span>
				) : fullWidth ? (
					<span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
				) : (
					label
				))}
			{/* Stacked, the label sits below the icon, so an inline badge would land under
			    both — it stays pinned to the icon's corner instead. */}
			{!!unread && (
				<span className={getBadgeClass(showLabel && !stacked)}>{unread > 99 ? "99+" : unread}</span>
			)}
		</>
	);

	const element = disabled ? (
		<span
			className={itemClass}
			aria-disabled="true"
			data-testid={(menuItem.textKey || menuItem.key || "menu-item").replaceAll(".", "-")}
			{...tip(tooltip)}
			aria-label={tooltip || label}
		>
			{content}
		</span>
	) : path ? (
		<Link
			to={path}
			className={itemClass}
			onMouseDown={isHeaderMoreMenuOpen ? handleMenuMouseDown : undefined}
			onClick={handleMenuClick}
			data-testid={(menuItem.textKey || menuItem.key || "menu-item").replaceAll(".", "-")}
			{...tip(tooltip)}
			aria-label={tooltip || label}
		>
			{content}
		</Link>
	) : (
		<button
			type="button"
			className={itemClass}
			onClick={onClick}
			data-testid={(menuItem.textKey || menuItem.key || "menu-item").replaceAll(".", "-")}
			{...tip(tooltip)}
			aria-label={tooltip || label}
		>
			{content}
		</button>
	);

	// On the rail the label is the only thing identifying the icon, so it always gets a
	// tooltip — including the active item, unlike the native-title path above.
	return collapsed ? <Tooltip label={railLabel}>{element}</Tooltip> : element;
};

export default memo(HeaderMenuItem);
